import { useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from 'store/auth';
import Dropdown from '../elements/Dropdown';
import ChartStatus from '../elements/ChartStatus';
import SingleBtn from '../elements/SingleBtn';
import styles from 'styles/components/common/nav/SubNav.module.scss';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { ROUTE_PATH } from 'constants/routePath';
import { ChartInfo } from 'constants/chart/common/chartInfo';
import { TSelectObject } from 'types/dropdown';
import {
  getPatientListByChart,
  getCourseInfoListByPatient,
  getChartRoundListByPatient,
  getChartTypeListByPatient,
} from 'apis/student';
import { getPatientSimpleList } from 'apis/patient';
import useModal from 'hooks/useModal';
import useGoBack from 'hooks/useGoBack';
import { getChartInfoListByPatient } from 'apis/professor';

type TProps = {
  mode?: string;
  setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
};
type TChartType = TSelectObject & {
  status?: string | null;
  evaluationChartId: number;
  evaluationRound?: number;
  isScored?: boolean;
};

const SubNav = ({ mode, setIsModalOpen }: TProps) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const chartTypePath = location.pathname.split('/')[2];
  const userInfo = useAtomValue(userInfoAtom);
  const isStudent = userInfo?.userRole === 'STUDENT';
  const subNavRef = useRef<HTMLDivElement | null>(null);

  const courseId = query.get('courseId');
  const courseInfoId = query.get('courseInfoId');
  const studentUserId = query.get('studentUserId');

  const { openModal } = useModal();
  const goBack = useGoBack();

  const [patientList, setPatientList] = useState<TSelectObject[]>([]);
  const [subjectList, setSubjectList] = useState<TSelectObject[]>([]);
  const [roundList, setRoundList] = useState<TSelectObject[]>([]);
  const [chartTypeList, setChartTypeList] = useState<TChartType[]>([]);
  const [patient, setPatient] = useState<TSelectObject | undefined>();
  const [subject, setSubject] = useState<TSelectObject | undefined>();
  const [round, setRound] = useState<TSelectObject | undefined>();
  const [chartType, setChartType] = useState<TChartType | undefined>();
  const [showPatientList, setShowPatientList] = useState(false);
  const [showSubjectList, setShowSubjectList] = useState(false);
  const [showRoundList, setShowRoundList] = useState(false);
  const [showChartList, setShowChartList] = useState(false);
  const [isChanging, setIsChanging] = useState(false);

  // 차트 페이지 조회 실패 에러 처리
  const onError = () =>
    openModal({
      title: '차트 조회 실패',
      text: '다시 시도해 주세요.',
      onSubmit: () => goBack(),
    });

  // 대상자 리스트 조회
  useQuery('getPatientSimpleList', getPatientSimpleList, {
    enabled: mode === 'patientDetail',
    onSuccess: ({ data }) => {
      const list = data.patientSimpleList.map((patient) => ({
        label: `${patient.name} ${patient.birthAndSex}`,
        value: patient.patientId,
      }));
      setPatientList(list);
      setPatient(list.find((patient) => patient.value === Number(id)));
    },
    onError: () =>
      openModal({
        title: '대상자 조회 실패',
        text: '다시 시도해 주세요.',
        onSubmit: () => navigate(ROUTE_PATH.student.PATIENT_MANAGEMENT),
      }),
  });

  useQuery('getPatientListByChart', getPatientListByChart, {
    enabled: mode === 'evaluation' && isStudent,
    onSuccess: ({ data }) => {
      const list = data.map((patient) => ({
        label: `${patient.name} ${patient.birthAndSex}`,
        value: patient.patientId,
      }));
      setPatientList(list);
      setPatient(
        list.find((patient) => patient.value === Number(query.get('patientId')))
      );
    },
    onError,
  });

  // 강의 리스트 조회
  useQuery(
    ['getCourseInfoListByPatient', patient],
    () =>
      getCourseInfoListByPatient({
        patientId: Number(patient?.value),
      }),
    {
      enabled: mode === 'evaluation' && isStudent && !!patient?.value,
      onSuccess: ({ data }) => {
        const list = data.map((subject) => ({
          label: subject.courseName,
          value: subject.courseInfoId,
        }));
        setSubjectList(list);
        !isChanging &&
          setSubject(
            list.find(
              (subject) => subject.value === Number(query.get('courseInfoId'))
            )
          );
      },
      onError,
    }
  );

  // 회차 리스트 조회
  useQuery(
    ['getChartRoundListByPatient', patient, subject],
    () =>
      getChartRoundListByPatient({
        patientId: Number(patient?.value),
        courseInfoId: Number(subject?.value),
      }),
    {
      enabled:
        mode === 'evaluation' &&
        isStudent &&
        !!patient?.value &&
        !!subject?.value,
      onSuccess: ({ data }) => {
        const list = data.map((round) => ({
          label: round.registeredChartName,
          value: round.chartRound,
        }));
        setRoundList(list);
        !isChanging &&
          setRound(
            list.find(
              (round) => round.value === Number(query.get('chartRound'))
            )
          );
      },
      onError,
    }
  );

  // 차트 종류 리스트 조회
  useQuery(
    ['getChartTypeListByPatient', patient, subject, round],
    () =>
      getChartTypeListByPatient({
        patientId: Number(patient?.value),
        courseInfoId: Number(subject?.value),
        chartRound: Number(round?.value),
      }),
    {
      enabled:
        mode === 'evaluation' &&
        isStudent &&
        !!patient?.value &&
        !!subject?.value &&
        !!round?.value,
      onSuccess: ({ data }) => {
        const list = data.map((chartType) => ({
          label: chartType.chartTypeName,
          value: chartType.chartTypeNum,
          status: chartType.chartSubmitStatus,
          evaluationChartId: chartType.evaluationChartId,
          evaluationRound: chartType.evaluationRound,
          isScored: chartType.isScored,
        }));
        const currentChartType = ChartInfo.find(
          (info) => info.route === `/${chartTypePath}`
        )?.type;
        if (!currentChartType) throw new Error('차트 종류를 찾을 수 없습니다.');
        setChartTypeList(list);
        !isChanging &&
          setChartType(
            list.find((chartType) => chartType.value === currentChartType)
          );
      },
      onError,
    }
  );

  useQuery(
    ['getChartInfoListByPatient'],
    () => getChartInfoListByPatient(Number(id)),
    {
      onSuccess: ({ data }) => {
        const patientDefaultValue = {
          label: data.evaluationChartInfo.patientInfo,
          value: 1,
        };
        setPatient(patientDefaultValue);
        const courseDefaultValue = {
          label: data.evaluationChartInfo.courseName,
          value: 1,
        };
        setSubject(courseDefaultValue);
        const roundDefaultValue = {
          label: data.evaluationChartInfo.chartRoundAndChartName,
          value: 1,
        };
        setRound(roundDefaultValue);

        const list = data.evaluationChartInfo.evaluationChartInfoActiveList.map(
          (chartInfo) => ({
            label: ChartInfo.find((info) => info.type === chartInfo.chartType)
              ?.name as string,
            value: chartInfo.chartType,
            evaluationChartId: chartInfo.evaluationChartId,
            evaluationRound: chartInfo.evaluationRound,
          })
        );
        setChartTypeList(list);

        const currentChartType = ChartInfo.find(
          (info) => info.route === `/${chartTypePath}`
        )?.type;
        !isChanging &&
          setChartType(
            list.find((chartType) => chartType.value === currentChartType)
          );
      },
      enabled: mode === 'feedback',
    }
  );

  return (
    <div className={styles.sub_nav}>
      <div className="flex relative gap24" ref={subNavRef}>
        {/* 대상자 정보 드롭다운 */}
        <Dropdown
          isSubNav={true}
          selectList={patientList}
          selectedObject={patient}
          showSelectList={showPatientList}
          setShowSelectList={setShowPatientList}
          defaultValue={patient?.label}
          btnStyle={{
            width: '217px',
          }}
          dropdownStyle={{ width: '240px', marginLeft: '-20px' }}
          placeholder="대상자 선택"
          onClickButton={() => setShowPatientList(!showPatientList)}
          onClickSelectItem={(selected) => {
            setShowPatientList(false);
            setPatient(selected);
            if (mode === 'patientDetail') {
              navigate(`${ROUTE_PATH.student.PATIENT}/${selected.value}`);
            } else {
              setIsChanging(true);
              setSubject(undefined);
              setRound(undefined);
              setChartType(undefined);
            }
          }}
          disabled={!isStudent}
        />
        {mode && ['evaluation', 'feedback'].includes(mode) && (
          <>
            {/* 강의명 드롭다운 */}
            <Dropdown
              isSubNav={true}
              selectList={subjectList}
              selectedObject={subject}
              showSelectList={showSubjectList}
              setShowSelectList={setShowSubjectList}
              defaultValue={subject?.label}
              btnStyle={{
                width: '200px',
              }}
              dropdownStyle={{ width: '240px', marginLeft: '-20px' }}
              placeholder="강의명 선택"
              onClickButton={() => setShowSubjectList(!showSubjectList)}
              onClickSelectItem={(selected) => {
                setShowSubjectList(false);
                setSubject(selected);
                setIsChanging(true);
                setRound(undefined);
                setChartType(undefined);
              }}
              disabled={!isStudent || !patient}
            />
            {/* 회차 드롭다운 */}
            <Dropdown
              isSubNav={true}
              selectList={roundList}
              selectedObject={round}
              showSelectList={showRoundList}
              setShowSelectList={setShowRoundList}
              defaultValue={round?.label}
              btnStyle={{
                width: '200px',
              }}
              dropdownStyle={{ width: '240px', marginLeft: '-20px' }}
              placeholder="(회차) 차트명 선택"
              onClickButton={() => setShowRoundList(!showRoundList)}
              onClickSelectItem={(selected) => {
                setShowRoundList(false);
                setRound(selected);
                setIsChanging(true);
                setChartType(undefined);
              }}
              disabled={!isStudent || !subject}
            />
            {/* 차트 종류 드롭다운 */}
            <Dropdown<TChartType>
              isSubNav={true}
              selectList={chartTypeList}
              selectedObject={chartType}
              showSelectList={showChartList}
              setShowSelectList={setShowChartList}
              defaultValue={chartType?.label}
              btnStyle={{
                width: '200px',
              }}
              dropdownStyle={{ width: '270px', marginLeft: '-20px' }}
              placeholder="차트 종류 선택"
              onClickButton={() => setShowChartList(!showChartList)}
              onClickSelectItem={(selected) => {
                setShowChartList(false);
                if (
                  patient?.value === Number(query.get('patientId')) &&
                  subject?.value === Number(query.get('courseInfoId')) &&
                  round?.value === Number(query.get('chartRound')) &&
                  selected.value === chartType?.value
                )
                  return;

                const chartRoute = ChartInfo.find(
                  (info) => info.type === selected.value
                )?.route;
                if (
                  !chartRoute ||
                  !selected.evaluationChartId ||
                  !patient?.value ||
                  !subject?.value ||
                  !round?.value
                ) {
                  openModal({
                    title: '차트 조회 실패',
                    text: '관리자에게 문의해 주세요.',
                  });
                  return;
                }
                setChartType(selected);
                setIsChanging(false);
                navigate(
                  isStudent
                    ? `/evaluation${chartRoute}/${selected.evaluationChartId}${
                        selected.status !== '제출완료' ? '/register' : ''
                      }?patientId=${patient?.value}&courseInfoId=${subject?.value}&chartRound=${round?.value}&tab=${
                        selected.evaluationRound === 2
                          ? selected.isScored
                            ? 3
                            : 2
                          : selected.isScored
                          ? 1
                          : 0
                      }`
                    : `/feedback${chartRoute}/${
                        selected.evaluationChartId
                      }?courseId=${courseId}&courseInfoId=${courseInfoId}&chartRound=${query.get(
                        'chartRound'
                      )}&studentUserId=${studentUserId}&tab=${
                        selected.evaluationRound === 2 ? 3 : 1
                      }`
                );
              }}
              disabled={!round}
            >
              {({ label, status }) => {
                return (
                  <>
                    <span>{label}</span>
                    {status && <ChartStatus status={status} />}
                  </>
                );
              }}
            </Dropdown>
          </>
        )}
      </div>

      {/* 대상자 상세정보에서만 보이도록 */}
      {mode === 'patientDetail' && (
        <SingleBtn
          type="button"
          btnText="+ 차트 생성"
          hasOutline={true}
          btnStyle={{
            height: '32px',
            padding: '0 16px',
            fontSize: '14px',
            color: STYLES_VARIABLES.GRAY_09,
          }}
          onClick={() => setIsModalOpen && setIsModalOpen(true)}
        />
      )}
    </div>
  );
};

export default SubNav;
